import {
  Atom,
  Beaker,
  Droplet,
  ShieldAlert,
  Zap,
  RotateCcw,
  Hexagon,
  type LucideIcon,
} from "@/lib/icons";
import { elements } from "@/data/elements";
import type { GhsPictogram } from "@/games/labSafety/questions";

/**
 * Flashcard data for the Learn tab.
 *
 * Decks are pure data so the Learn UI stays a thin renderer. Each card
 * has a stable `id` (used as the Leitner progress key) and at least one
 * accepted answer plus optional aliases, so "Iron" and "Fe" can both
 * pass an Element-symbol card in Type mode.
 *
 * Adding a deck:
 *   1. Build a `FlashCard[]` array with stable ids.
 *   2. Add a `Deck` entry to `STARTER_DECKS`.
 *   3. That's it - the hub picks it up automatically.
 */

export type CardKind = "text" | "pictogram";

export interface FlashCard {
  id: string;
  /** Question shown on the card front. */
  prompt: string;
  /** Canonical answer shown on the back. */
  answer: string;
  /** Other strings accepted in Type mode (case-insensitive, trimmed). */
  aliases?: string[];
  /** Short context shown under the answer (one sentence). */
  hint?: string;
  /** Card type. Pictogram cards render the GHS icon as the prompt. */
  kind?: CardKind;
  /** Pictogram id when kind === "pictogram". */
  pictogram?: GhsPictogram;
}

export interface Deck {
  id: string;
  title: string;
  /** One-line description shown in the deck list. */
  description: string;
  icon: LucideIcon;
  /** Whether Type mode is allowed (some decks only make sense as Flip). */
  allowType: boolean;
  cards: FlashCard[];
}

// ---------------- Element symbols ----------------
// Symbol -> name. Z 1-54 keeps the deck classroom-sized and excludes
// obscure radioactives most students don't see in HS chemistry.
const elementSymbolCards: FlashCard[] = elements
  .filter((e) => e.number <= 54)
  .map((e) => ({
    id: `el-sym-${e.number}`,
    prompt: e.symbol,
    answer: e.name,
    aliases: [String(e.number)],
    hint: `Atomic number ${e.number} - period ${e.period}, ${e.category.replace("-", " ")}.`,
  }));

// ---------------- Polyatomic ions ----------------
// Common HS-curriculum polyatomics. Each card shows the formula; the
// learner names it (and its charge in the answer line).
const polyatomicIonCards: FlashCard[] = [
  { formula: "NH4+", display: "NH\u2084\u207a", name: "Ammonium", charge: "+1" },
  { formula: "OH-", display: "OH\u207b", name: "Hydroxide", charge: "\u22121" },
  { formula: "NO3-", display: "NO\u2083\u207b", name: "Nitrate", charge: "\u22121" },
  { formula: "NO2-", display: "NO\u2082\u207b", name: "Nitrite", charge: "\u22121" },
  { formula: "CO3 2-", display: "CO\u2083\u00b2\u207b", name: "Carbonate", charge: "\u22122" },
  { formula: "HCO3-", display: "HCO\u2083\u207b", name: "Hydrogen carbonate", charge: "\u22121", alt: "Bicarbonate" },
  { formula: "SO4 2-", display: "SO\u2084\u00b2\u207b", name: "Sulfate", charge: "\u22122" },
  { formula: "SO3 2-", display: "SO\u2083\u00b2\u207b", name: "Sulfite", charge: "\u22122" },
  { formula: "PO4 3-", display: "PO\u2084\u00b3\u207b", name: "Phosphate", charge: "\u22123" },
  { formula: "ClO-", display: "ClO\u207b", name: "Hypochlorite", charge: "\u22121" },
  { formula: "ClO2-", display: "ClO\u2082\u207b", name: "Chlorite", charge: "\u22121" },
  { formula: "ClO3-", display: "ClO\u2083\u207b", name: "Chlorate", charge: "\u22121" },
  { formula: "ClO4-", display: "ClO\u2084\u207b", name: "Perchlorate", charge: "\u22121" },
  { formula: "MnO4-", display: "MnO\u2084\u207b", name: "Permanganate", charge: "\u22121" },
  { formula: "Cr2O7 2-", display: "Cr\u2082O\u2087\u00b2\u207b", name: "Dichromate", charge: "\u22122" },
  { formula: "CrO4 2-", display: "CrO\u2084\u00b2\u207b", name: "Chromate", charge: "\u22122" },
  { formula: "C2H3O2-", display: "C\u2082H\u2083O\u2082\u207b", name: "Acetate", charge: "\u22121" },
  { formula: "CN-", display: "CN\u207b", name: "Cyanide", charge: "\u22121" },
].map((p) => ({
  id: `poly-${p.formula.replace(/[^A-Za-z0-9]/g, "")}`,
  prompt: p.display,
  answer: `${p.name} (${p.charge})`,
  aliases: [p.name, p.name.toLowerCase(), ...((p as { alt?: string }).alt ? [(p as { alt: string }).alt] : [])],
  hint: "Polyatomic ion - learn name and charge together.",
}));

// ---------------- Oxidation states ----------------
// Most-common oxidation state for each ion the curriculum expects.
const oxidationStateCards: FlashCard[] = [
  { sym: "Na", state: "+1" },
  { sym: "K", state: "+1" },
  { sym: "Ag", state: "+1" },
  { sym: "Mg", state: "+2" },
  { sym: "Ca", state: "+2" },
  { sym: "Ba", state: "+2" },
  { sym: "Zn", state: "+2" },
  { sym: "Al", state: "+3" },
  { sym: "Fe", state: "+2 or +3" },
  { sym: "Cu", state: "+1 or +2" },
  { sym: "Pb", state: "+2 or +4" },
  { sym: "Sn", state: "+2 or +4" },
  { sym: "F", state: "\u22121" },
  { sym: "Cl", state: "\u22121" },
  { sym: "Br", state: "\u22121" },
  { sym: "I", state: "\u22121" },
  { sym: "O", state: "\u22122" },
  { sym: "S", state: "\u22122" },
  { sym: "N", state: "\u22123" },
  { sym: "P", state: "\u22123" },
].map((o) => ({
  id: `ox-${o.sym}`,
  prompt: `Common oxidation state of ${o.sym}?`,
  answer: o.state,
  aliases: [o.state.replace("\u22121", "-1").replace("\u22122", "-2").replace("\u22123", "-3")],
  hint: "Most common ionic charge in school-level chemistry.",
}));

// ---------------- Acid/base indicators ----------------
const indicatorCards: FlashCard[] = [
  {
    id: "ind-litmus-acid",
    prompt: "Litmus paper in acid",
    answer: "Red",
    hint: "Blue litmus turns red in acidic solutions.",
  },
  {
    id: "ind-litmus-base",
    prompt: "Litmus paper in base",
    answer: "Blue",
    hint: "Red litmus turns blue in basic solutions.",
  },
  {
    id: "ind-phen-acid",
    prompt: "Phenolphthalein in acid",
    answer: "Colourless",
    aliases: ["colorless", "clear"],
    hint: "Phenolphthalein has no colour below pH ~8.2.",
  },
  {
    id: "ind-phen-base",
    prompt: "Phenolphthalein in base",
    answer: "Pink",
    aliases: ["magenta", "fuchsia"],
    hint: "Turns pink above pH ~10 - classic titration endpoint.",
  },
  {
    id: "ind-mo-acid",
    prompt: "Methyl orange in acid",
    answer: "Red",
    hint: "Below pH ~3.1 methyl orange is red.",
  },
  {
    id: "ind-mo-base",
    prompt: "Methyl orange in base",
    answer: "Yellow",
    hint: "Above pH ~4.4 methyl orange is yellow.",
  },
  {
    id: "ind-ub-acid",
    prompt: "Universal indicator at pH 2",
    answer: "Red",
    hint: "Strong acid range - red/orange.",
  },
  {
    id: "ind-ub-neutral",
    prompt: "Universal indicator at pH 7",
    answer: "Green",
    hint: "Neutral - the colour to memorise.",
  },
  {
    id: "ind-ub-base",
    prompt: "Universal indicator at pH 13",
    answer: "Purple",
    aliases: ["violet"],
    hint: "Strong base range - blue/purple.",
  },
];

// ---------------- GHS pictograms ----------------
const ghsCards: FlashCard[] = [
  { pict: "flame", name: "Flammable", hint: "Catches fire easily - keep away from sparks and open flame." },
  { pict: "exclamation", name: "Irritant", hint: "Skin/eye irritation, narcotic effects, respiratory irritation." },
  { pict: "skull", name: "Acute toxicity", hint: "Fatal or toxic in small amounts - handle in a fume hood." },
  { pict: "corrosion", name: "Corrosive", hint: "Causes skin burns or metal corrosion - PPE required." },
  { pict: "health-hazard", name: "Health hazard", hint: "Carcinogen, mutagen, or respiratory sensitiser - long-term exposure risk." },
  { pict: "environment", name: "Environmental hazard", hint: "Toxic to aquatic life - never pour down the drain." },
  { pict: "exploding-bomb", name: "Explosive", hint: "Unstable - shock or heat can detonate." },
  { pict: "flame-over-circle", name: "Oxidiser", hint: "Intensifies fire - keep away from combustibles." },
  { pict: "gas-cylinder", name: "Gas under pressure", hint: "Compressed/liquefied gas - cylinder may explode if heated." },
].map((g) => ({
  id: `ghs-${g.pict}`,
  prompt: g.name, // unused for kind=pictogram; UI renders the icon
  answer: g.name,
  aliases: [g.name.toLowerCase()],
  hint: g.hint,
  kind: "pictogram" as const,
  pictogram: g.pict as GhsPictogram,
}));

// ---------------- Organic functional groups ----------------
// Identify the functional-group family from its general structure.
const functionalGroupCards: FlashCard[] = [
  {
    suffix: "ane",
    prompt: "C\u2013C single bonds only (e.g. CH\u2083\u2013CH\u2083)",
    name: "Alkane",
    aliases: ["alkanes", "-ane"],
    hint: "Saturated hydrocarbons - suffix -ane.",
  },
  {
    suffix: "ene",
    prompt: "Contains C=C double bond (e.g. CH\u2082=CH\u2082)",
    name: "Alkene",
    aliases: ["alkenes", "-ene"],
    hint: "Unsaturated - reacts via addition.",
  },
  {
    suffix: "yne",
    prompt: "Contains C\u2261C triple bond (e.g. HC\u2261CH)",
    name: "Alkyne",
    aliases: ["alkynes", "-yne"],
    hint: "Two pi bonds - very reactive.",
  },
  {
    suffix: "ol",
    prompt: "R\u2013OH",
    name: "Alcohol",
    aliases: ["alcohols", "hydroxyl", "-ol"],
    hint: "Hydroxyl group - suffix -ol.",
  },
  {
    suffix: "al",
    prompt: "R\u2013CHO",
    name: "Aldehyde",
    aliases: ["aldehydes", "-al"],
    hint: "Carbonyl at the end of the chain - suffix -al.",
  },
  {
    suffix: "one",
    prompt: "R\u2013CO\u2013R'",
    name: "Ketone",
    aliases: ["ketones", "-one"],
    hint: "Carbonyl between two carbons - suffix -one.",
  },
  {
    suffix: "oic",
    prompt: "R\u2013COOH",
    name: "Carboxylic acid",
    aliases: ["carboxylic acids", "carboxyl", "-oic acid"],
    hint: "Carboxyl group - weakly acidic.",
  },
  {
    suffix: "oate",
    prompt: "R\u2013COO\u2013R'",
    name: "Ester",
    aliases: ["esters", "-oate"],
    hint: "Formed from acid + alcohol - often fruity smell.",
  },
  {
    suffix: "amine",
    prompt: "R\u2013NH\u2082",
    name: "Amine",
    aliases: ["amines", "amino"],
    hint: "Nitrogen with lone pair - basic.",
  },
  {
    suffix: "ether",
    prompt: "R\u2013O\u2013R'",
    name: "Ether",
    aliases: ["ethers"],
    hint: "Oxygen between two carbons - generally unreactive.",
  },
].map((f) => ({
  id: `fg-${f.suffix}`,
  prompt: f.prompt,
  answer: f.name,
  aliases: f.aliases,
  hint: f.hint,
}));

// ---------------- Registry ----------------

export const STARTER_DECKS: Deck[] = [
  {
    id: "element-symbols",
    title: "Element Symbols",
    description: "Z 1-54: name the element from its symbol.",
    icon: Atom,
    allowType: true,
    cards: elementSymbolCards,
  },
  {
    id: "polyatomic-ions",
    title: "Polyatomic Ions",
    description: "Name the ion (and its charge) from its formula.",
    icon: Beaker,
    allowType: true,
    cards: polyatomicIonCards,
  },
  {
    id: "oxidation-states",
    title: "Oxidation States",
    description: "Common ionic charges for school-level chemistry.",
    icon: Zap,
    allowType: true,
    cards: oxidationStateCards,
  },
  {
    id: "acid-base-indicators",
    title: "Acid/Base Indicators",
    description: "Litmus, phenolphthalein, methyl orange, universal indicator.",
    icon: Droplet,
    allowType: true,
    cards: indicatorCards,
  },
  {
    id: "ghs-pictograms",
    title: "GHS Pictograms",
    description: "Identify the hazard symbol.",
    icon: ShieldAlert,
    allowType: true,
    cards: ghsCards,
  },
  {
    id: "functional-groups",
    title: "Functional Groups",
    description: "Name the organic functional group from its structure.",
    icon: Hexagon,
    allowType: true,
    cards: functionalGroupCards,
  },
];

/**
 * The "Missed from Play" deck is dynamic - cards are added at runtime by
 * games (Element Battle, Electron Config Sprint) when the player gets
 * something wrong. Storage key is shared across the app so any game can
 * append. See `src/learn/missedFromPlay.ts`.
 */
export const MISSED_DECK_ID = "missed-from-play";

export const MISSED_DECK_META: Omit<Deck, "cards"> = {
  id: MISSED_DECK_ID,
  title: "Missed from Play",
  description: "Cards built from elements you missed in Element Battle and Electron Config Sprint.",
  icon: RotateCcw,
  allowType: true,
};

export function getDeck(id: string, missedCards: FlashCard[]): Deck | null {
  if (id === MISSED_DECK_ID) {
    return { ...MISSED_DECK_META, cards: missedCards };
  }
  return STARTER_DECKS.find((d) => d.id === id) ?? null;
}

/** Loose answer match: trim, case-fold, ignore punctuation/whitespace. */
export function answerMatches(input: string, card: FlashCard): boolean {
  const norm = (s: string) =>
    s.toLowerCase().replace(/\s+/g, " ").replace(/[.,;:!?'"`()]/g, "").trim();
  const candidates = [card.answer, ...(card.aliases ?? [])].map(norm);
  const i = norm(input);
  if (!i) return false;
  return candidates.includes(i);
}